import {
  Fragment,
  useMemo,
  useState,
} from "react";

import {
  useQuery,
} from "@tanstack/react-query";

import {
  api,
} from "@/lib/api";

import {
  useFarm,
} from "@/context/FarmContext";

import {
  PageHeader,
  NoFarm,
} from "@/components/Page";

import {
  Card,
} from "@/components/ui/card";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  ChevronDown,
  ChevronRight,
  Boxes,
  Leaf,
  CalendarDays,
  Wheat,
  Info,
  BarChart3,
  Clock3,
} from "lucide-react";

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";


const STATUS = {
  critical: {
    label: "Critical",
    cls:
      "bg-destructive/10 text-destructive",
    colour:
      "#dc2626",
  },

  low: {
    label: "Low",
    cls:
      "bg-orange-100 text-orange-800",
    colour:
      "#ea580c",
  },

  watch: {
    label: "Watch",
    cls:
      "bg-amber-100 text-amber-800",
    colour:
      "#d97706",
  },

  ok: {
    label: "OK",
    cls:
      "bg-primary/10 text-primary",
    colour:
      "#2f8f57",
  },

  not_used: {
    label: "Not in ration",
    cls:
      "bg-secondary text-muted-foreground",
    colour:
      "#94a3b8",
  },

  unknown: {
    label: "Unknown",
    cls:
      "bg-secondary text-muted-foreground",
    colour:
      "#94a3b8",
  },
};


function safeNumber(
  value,
  fallback = 0
) {
  const number =
    Number(value);

  return Number.isFinite(
    number
  )
    ? number
    : fallback;
}


function formatNumber(
  value,
  decimals = 1
) {
  if (
    value === null ||
    value === undefined
  ) {
    return "—";
  }


  const number =
    Number(value);


  if (
    !Number.isFinite(
      number
    )
  ) {
    return "—";
  }


  return number.toLocaleString(
    "en-IE",
    {
      minimumFractionDigits:
        decimals,

      maximumFractionDigits:
        decimals,
    }
  );
}


function formatDate(
  value
) {
  if (!value) {
    return "—";
  }


  const date =
    new Date(value);


  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "—";
  }


  return date.toLocaleDateString(
    "en-IE",
    {
      day:
        "numeric",

      month:
        "short",

      year:
        "numeric",
    }
  );
}


/*
 * IMPORTANT:
 * The feed-budget API already gives us
 * group.tonnes_per_day.
 *
 * This is therefore the most reliable source
 * for current fresh-feed usage.
 */
function getDailyFreshTonnes(
  row
) {
  const groups =
    Array.isArray(
      row?.groups
    )
      ? row.groups
      : [];


  const fromGroups =
    groups.reduce(
      (
        total,
        group
      ) =>
        total +
        safeNumber(
          group.tonnes_per_day,
          0
        ),
      0
    );


  if (
    fromGroups > 0
  ) {
    return fromGroups;
  }


  /*
   * Fallbacks in case the backend later exposes
   * a feed-level daily fresh value.
   */
  const directTonnes =
    safeNumber(
      row?.tonnes_per_day,
      0
    );


  if (
    directTonnes > 0
  ) {
    return directTonnes;
  }


  const directKg =
    safeNumber(
      row?.daily_fresh_kg ??
      row?.daily_fresh_use_kg ??
      row?.daily_fresh,
      0
    );


  if (
    directKg > 0
  ) {
    return (
      directKg /
      1000
    );
  }


  return 0;
}


/*
 * Prefer an explicit stock property if the
 * backend provides one.
 *
 * Otherwise:
 * stock = days remaining × current use/day
 *
 * This is mathematically consistent with the
 * existing Feed Budget calculation.
 */
function getCurrentStockTonnes(
  row
) {
  const possibleValues = [
    row?.tonnes_fresh,
    row?.stock_tonnes_fresh,
    row?.current_stock_tonnes,
  ];


  for (
    const value
    of possibleValues
  ) {
    const number =
      Number(value);


    if (
      Number.isFinite(
        number
      )
    ) {
      return number;
    }
  }


  const stockKg =
    Number(
      row?.stock_fresh_kg
    );


  if (
    Number.isFinite(
      stockKg
    )
  ) {
    return (
      stockKg /
      1000
    );
  }


  const dailyUse =
    getDailyFreshTonnes(
      row
    );


  const days =
    Number(
      row?.days_remaining
    );


  if (
    dailyUse > 0 &&
    Number.isFinite(
      days
    )
  ) {
    return (
      dailyUse *
      days
    );
  }


  return null;
}


function calculateRunOutDate(
  row
) {
  if (
    row?.run_out_date
  ) {
    return row.run_out_date;
  }


  const days =
    Number(
      row?.days_remaining
    );


  if (
    !Number.isFinite(
      days
    ) ||
    days < 0
  ) {
    return null;
  }


  const date =
    new Date();


  date.setDate(
    date.getDate() +
    Math.round(
      days
    )
  );


  return date.toISOString();
}


function SummaryMetric({
  icon: Icon,
  value,
  unit,
  label,
  hint,
}) {
  return (
    <div className="flex items-center gap-3 min-w-0">

      <div className="h-11 w-11 rounded-full bg-primary/10 grid place-items-center shrink-0">

        <Icon className="h-5 w-5 text-primary" />

      </div>


      <div className="min-w-0">

        <div className="flex items-baseline gap-1 flex-wrap">

          <p className="font-display text-xl sm:text-2xl font-extrabold text-[hsl(148,38%,16%)] tabular-nums">

            {value}

          </p>


          {unit && (

            <span className="text-xs font-semibold text-muted-foreground">
              {unit}
            </span>

          )}

        </div>


        <p className="text-xs font-semibold text-[hsl(148,38%,16%)]">
          {label}
        </p>


        {hint && (

          <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5 truncate">
            {hint}
          </p>

        )}

      </div>

    </div>
  );
}


function StockTooltip({
  active,
  payload,
}) {
  if (
    !active ||
    !payload?.length
  ) {
    return null;
  }


  const item =
    payload[0]
      ?.payload;


  if (!item) {
    return null;
  }


  return (
    <div className="rounded-xl border bg-background p-3 shadow-lg">

      <p className="font-semibold text-sm text-[hsl(148,38%,16%)]">
        {item.name}
      </p>


      <div className="mt-2 space-y-1 text-xs text-muted-foreground">

        <p>
          Stock:{" "}
          <strong className="text-foreground">
            {formatNumber(
              item.stock,
              1
            )} t
          </strong>
        </p>


        <p>
          Use:{" "}
          <strong className="text-foreground">
            {formatNumber(
              item.dailyFresh,
              2
            )} t/day
          </strong>
        </p>


        <p>
          Days remaining:{" "}
          <strong className="text-foreground">
            {formatNumber(
              item.days,
              1
            )}
          </strong>
        </p>

      </div>

    </div>
  );
}


export default function FeedBudget() {
  const {
    farmId,
    loading,
  } =
    useFarm();


  const currentYear =
    new Date().getFullYear();


  const [
    historyYear,
    setHistoryYear,
  ] =
    useState(
      currentYear
    );


  const [
    historyFeed,
    setHistoryFeed,
  ] =
    useState(
      "all"
    );


  const [
    historyMetric,
    setHistoryMetric,
  ] =
    useState(
      "fresh"
    );


  const {
    data: historyData,
    isFetching: historyFetching,
    isError: historyError,
  } =
    useQuery({
      queryKey: [
        "feed-usage-history",
        farmId,
        historyYear,
      ],

      queryFn:
        async () =>
          (
            await api.get(
              `/farms/${farmId}/feed-usage/history`,
              {
                params: {
                  year:
                    historyYear,
                },
              }
            )
          ).data,

      enabled:
        Boolean(
          farmId
        ),

      refetchOnWindowFocus:
        true,
    });


  const [
    expanded,
    setExpanded,
  ] =
    useState({});


  const {
    data,
    isError,
    error,
    isFetching,
  } =
    useQuery({
      queryKey: [
        "feed-budget",
        farmId,
      ],

      queryFn:
        async () =>
          (
            await api.get(
              `/farms/${farmId}/feed-budget`
            )
          ).data,

      enabled:
        Boolean(
          farmId
        ),

      refetchInterval:
        5 *
        60 *
        1000,

      refetchOnWindowFocus:
        true,
    });


  const rows =
    Array.isArray(
      data?.feeds
    )
      ? data.feeds
      : [];


  const totals =
    data?.totals ||
    {};


  const pasture =
    data?.pasture_supply;


  /*
   * All stored feeds.
   * Grazed grass is already handled by pasture_supply.
   */
  const storedRows =
    rows.filter(
      (
        row
      ) =>
        row.status !==
        "unknown"
    );


  /*
   * Actual feeds currently used in an active ration.
   *
   * We deliberately use groups[].tonnes_per_day here.
   * This fixes the bug in the previous version.
   */
  const feedsInUse =
    useMemo(
      () =>
        storedRows.filter(
          (
            row
          ) =>
            getDailyFreshTonnes(
              row
            ) > 0
        ),
      [
        storedRows,
      ]
    );


  const totalFreshUseTonnesDay =
    useMemo(
      () =>
        feedsInUse.reduce(
          (
            total,
            row
          ) =>
            total +
            getDailyFreshTonnes(
              row
            ),
          0
        ),
      [
        feedsInUse,
      ]
    );


  const outlookRows =
    useMemo(
      () =>
        feedsInUse
          .map(
            (
              row
            ) => ({
              ...row,

              dailyFresh:
                getDailyFreshTonnes(
                  row
                ),

              stock:
                getCurrentStockTonnes(
                  row
                ),

              runOut:
                calculateRunOutDate(
                  row
                ),
            })
          )
          .sort(
            (
              a,
              b
            ) =>
              safeNumber(
                a.days_remaining,
                999999
              ) -
              safeNumber(
                b.days_remaining,
                999999
              )
          ),
      [
        feedsInUse,
      ]
    );


  const chartData =
    outlookRows
      .filter(
        (
          row
        ) =>
          row.days_remaining !=
          null
      )
      .map(
        (
          row
        ) => ({
          id:
            row.id,

          name:
            row.name,

          days:
            safeNumber(
              row.days_remaining,
              0
            ),

          dailyFresh:
            row.dailyFresh,

          stock:
            row.stock,

          status:
            row.status,

          colour:
            (
              STATUS[
                row.status
              ] ||
              STATUS.ok
            ).colour,
        })
      );


  const shortest =
    outlookRows[0] ||
    null;


  const historyFeeds =
    Array.isArray(
      historyData?.feeds
    )
      ? historyData.feeds
      : [];


  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];


  const selectedHistoryFeed =
    historyFeed === "all"
      ? null
      : historyFeeds.find(
          (
            feed
          ) =>
            feed.feed_id ===
            historyFeed
        );


  const getHistoryValue =
    (
      month
    ) => {
      if (
        historyMetric ===
        "dm"
      ) {
        return safeNumber(
          month?.dm_tonnes,
          0
        );
      }


      if (
        historyMetric ===
        "cost"
      ) {
        return safeNumber(
          month?.cost,
          0
        );
      }


      return safeNumber(
        month?.fresh_tonnes,
        0
      );
    };


  const historyChartData =
    monthNames.map(
      (
        name,
        index
      ) => {
        const monthKey =
          String(
            index + 1
          );


        const month =
          selectedHistoryFeed
            ? selectedHistoryFeed
                ?.months?.[
                  monthKey
                ]
            : historyData
                ?.months?.[
                  monthKey
                ];


        return {
          month:
            name,

          value:
            getHistoryValue(
              month
            ),
        };
      }
    );


  const historyTotal =
    selectedHistoryFeed
      ? (
          historyMetric ===
          "dm"
            ? safeNumber(
                selectedHistoryFeed
                  .dm_tonnes,
                0
              )
            : historyMetric ===
              "cost"
            ? safeNumber(
                selectedHistoryFeed
                  .cost,
                0
              )
            : safeNumber(
                selectedHistoryFeed
                  .fresh_tonnes,
                0
              )
        )
      : (
          historyMetric ===
          "dm"
            ? safeNumber(
                historyData
                  ?.totals
                  ?.dm_tonnes,
                0
              )
            : historyMetric ===
              "cost"
            ? safeNumber(
                historyData
                  ?.totals
                  ?.cost,
                0
              )
            : safeNumber(
                historyData
                  ?.totals
                  ?.fresh_tonnes,
                0
              )
        );


  const availableYears =
    Array.from(
      {
        length:
          Math.max(
            currentYear -
              2025 +
              1,
            1
          ),
      },
      (
        _,
        index
      ) =>
        currentYear -
        index
    );


  const toggle =
    (
      id
    ) => {
      setExpanded(
        (
          current
        ) => ({
          ...current,

          [id]:
            !current[
              id
            ],
        })
      );
    };


  if (
    !loading &&
    !farmId
  ) {
    return (
      <NoFarm />
    );
  }


  if (
    !data &&
    !isError
  ) {
    return (
      <div className="text-muted-foreground">
        Loading feed budget…
      </div>
    );
  }


  if (
    isError
  ) {
    return (
      <div className="text-muted-foreground">

        Feed budget unavailable.

        {" "}

        {error?.message
          ? `(${error.message})`
          : ""}

      </div>
    );
  }


  return (
    <div>

      <PageHeader
        title="Feed Budget"
        subtitle="Live pasture availability, current feed usage, stock outlook and usage history."
      />


      {isFetching && (

        <p className="text-xs text-muted-foreground mb-3">
          Updating feed stock and ration-use calculations…
        </p>

      )}


      {/* ================================================= */}
      {/* TOP SUMMARY                                       */}
      {/* ================================================= */}

      <Card className="p-4 sm:p-5 mb-6">

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:divide-x">

          <SummaryMetric
            icon={Leaf}
            value={formatNumber(
              totals.pasture_available_kg_dm ??
              pasture?.available_grass_kg_dm,
              0
            )}
            unit="kg DM"
            label="Pasture available"
            hint="Live grazed grass"
          />


          <div className="lg:pl-5">

            <SummaryMetric
              icon={Boxes}
              value={formatNumber(
                totals.tonnes_dm_remaining,
                1
              )}
              unit="t DM"
              label="Stored feed DM"
              hint="Physical inventory"
            />

          </div>


          <div className="lg:pl-5">

            <SummaryMetric
              icon={Wheat}
              value={formatNumber(
                totalFreshUseTonnesDay,
                2
              )}
              unit="t/day"
              label="Feed used today"
              hint="Stored feeds"
            />

          </div>


          <div className="lg:pl-5">

            <SummaryMetric
              icon={Clock3}
              value={
                shortest
                  ? formatNumber(
                      shortest.days_remaining,
                      1
                    )
                  : "—"
              }
              unit={
                shortest
                  ? "days"
                  : ""
              }
              label="Shortest stock"
              hint={
                shortest
                  ?.name ||
                "No stored feed in use"
              }
            />

          </div>

        </div>

      </Card>


      {/* ================================================= */}
      {/* PASTURE                                          */}
      {/* ================================================= */}

      {pasture?.available && (

        <Card className="p-5 sm:p-6 mb-6 border-primary/20 bg-primary/[0.025]">

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">

            <div className="flex items-start gap-3">

              <div className="h-11 w-11 rounded-full bg-primary/10 grid place-items-center shrink-0">

                <Leaf className="h-5 w-5 text-primary" />

              </div>


              <div>

                <h2 className="font-display font-bold text-lg text-[hsl(148,38%,16%)]">
                  Pasture supply
                </h2>


                <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                  Pasture is included here for overall feed security. Detailed covers and grass growth remain in Paddock & Grass.
                </p>

              </div>

            </div>


            <div className="lg:text-right shrink-0">

              <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">
                Pasture days available
              </p>


              <p className="font-display text-3xl font-extrabold text-[hsl(148,38%,16%)] tabular-nums">
                {formatNumber(
                  pasture.days_available,
                  1
                )}
              </p>


              <p className="text-xs text-muted-foreground">
                At current ration demand
              </p>

            </div>

          </div>


          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">

            <div className="rounded-xl border bg-background/80 p-4">

              <p className="text-xs text-muted-foreground">
                Available pasture
              </p>


              <p className="font-display text-xl font-bold mt-1">
                {formatNumber(
                  pasture.available_grass_kg_dm,
                  0
                )}{" "}

                <span className="text-xs text-muted-foreground">
                  kg DM
                </span>
              </p>

            </div>


            <div className="rounded-xl border bg-background/80 p-4">

              <p className="text-xs text-muted-foreground">
                Daily grass demand
              </p>


              <p className="font-display text-xl font-bold mt-1">
                {formatNumber(
                  pasture.daily_demand_kg_dm,
                  0
                )}{" "}

                <span className="text-xs text-muted-foreground">
                  kg DM/day
                </span>
              </p>

            </div>


            <div className="rounded-xl border bg-background/80 p-4">

              <p className="text-xs text-muted-foreground">
                Fresh pasture equivalent
              </p>


              <p className="font-display text-xl font-bold mt-1">
                {formatNumber(
                  pasture.fresh_equivalent_tonnes,
                  1
                )}{" "}

                <span className="text-xs text-muted-foreground">
                  t
                </span>
              </p>

            </div>

          </div>

        </Card>

      )}


      {/* ================================================= */}
      {/* FEED OUTLOOK — CHART + TABLE                     */}
      {/* ================================================= */}

      <Card className="p-5 sm:p-6 mb-6">

        <div className="flex items-start gap-3 mb-6">

          <div className="h-10 w-10 rounded-full bg-primary/10 grid place-items-center shrink-0">

            <BarChart3 className="h-5 w-5 text-primary" />

          </div>


          <div>

            <h2 className="font-display text-lg font-bold text-[hsl(148,38%,16%)]">
              Feed outlook
            </h2>


            <p className="text-sm text-muted-foreground mt-1">
              At the current active-ration feeding rate, this is how long each stored feed will last.
            </p>

          </div>

        </div>


        {chartData.length >
        0 ? (

          <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">

            {/* CHART */}

            <div className="xl:col-span-5 min-w-0">

              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground mb-3">
                Days remaining
              </p>


              <div
                className="w-full"
                style={{
                  height:
                    Math.max(
                      260,
                      chartData.length *
                        58
                    ),
                }}
              >

                <ResponsiveContainer
                  width="100%"
                  height="100%"
                >

                  <BarChart
                    data={chartData}
                    layout="vertical"
                    margin={{
                      top:
                        5,

                      right:
                        45,

                      bottom:
                        5,

                      left:
                        5,
                    }}
                  >

                    <CartesianGrid
                      strokeDasharray="3 3"
                      horizontal={false}
                      opacity={0.35}
                    />


                    <XAxis
                      type="number"
                      axisLine={false}
                      tickLine={false}
                      tick={{
                        fontSize:
                          10,
                      }}
                    />


                    <YAxis
                      type="category"
                      dataKey="name"
                      width={125}
                      axisLine={false}
                      tickLine={false}
                      tick={{
                        fontSize:
                          11,
                      }}
                    />


                    <Tooltip
                      content={
                        <StockTooltip />
                      }
                    />


                    <Bar
                      dataKey="days"
                      radius={[
                        0,
                        6,
                        6,
                        0,
                      ]}
                      barSize={22}
                    >

                      {chartData.map(
                        (
                          item
                        ) => (

                          <Cell
                            key={
                              item.id
                            }
                            fill={
                              item.colour
                            }
                          />

                        )
                      )}


                      <LabelList
                        dataKey="days"
                        position="right"
                        formatter={(
                          value
                        ) =>
                          `${formatNumber(
                            value,
                            0
                          )} d`
                        }
                        style={{
                          fontSize:
                            11,

                          fontWeight:
                            600,
                        }}
                      />

                    </Bar>

                  </BarChart>

                </ResponsiveContainer>

              </div>

            </div>


            {/* TABLE */}

            <div className="xl:col-span-7 min-w-0">

              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground mb-3">
                Current feed position
              </p>


              <div className="rounded-xl border overflow-x-auto">

                <Table>

                  <TableHeader>

                    <TableRow>

                      <TableHead>
                        Feed
                      </TableHead>

                      <TableHead className="text-right">
                        Current stock
                      </TableHead>

                      <TableHead className="text-right">
                        Use / day
                      </TableHead>

                      <TableHead className="text-right">
                        Days left
                      </TableHead>

                      <TableHead className="text-right">
                        Est. run-out
                      </TableHead>

                    </TableRow>

                  </TableHeader>


                  <TableBody>

                    {outlookRows.map(
                      (
                        row
                      ) => {
                        const status =
                          STATUS[
                            row.status
                          ] ||
                          STATUS.unknown;


                        return (
                          <TableRow
                            key={
                              row.id
                            }
                          >

                            <TableCell>

                              <div className="flex flex-wrap items-center gap-2">

                                <span className="font-semibold text-[hsl(148,38%,16%)]">
                                  {row.name}
                                </span>


                                <span className={`rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${status.cls}`}>
                                  {status.label}
                                </span>

                              </div>

                            </TableCell>


                            <TableCell className="text-right tabular-nums">

                              {row.stock !=
                              null
                                ? `${formatNumber(
                                    row.stock,
                                    1
                                  )} t`
                                : "—"}

                            </TableCell>


                            <TableCell className="text-right tabular-nums">

                              {formatNumber(
                                row.dailyFresh,
                                2
                              )}{" "}

                              t

                            </TableCell>


                            <TableCell className="text-right tabular-nums font-bold">

                              {formatNumber(
                                row.days_remaining,
                                1
                              )}{" "}

                              d

                            </TableCell>


                            <TableCell className="text-right tabular-nums text-muted-foreground">

                              {formatDate(
                                row.runOut
                              )}

                            </TableCell>

                          </TableRow>
                        );
                      }
                    )}

                  </TableBody>

                </Table>

              </div>


              <p className="text-xs text-muted-foreground mt-3">
                Use is calculated from active rations and current animal numbers.
              </p>

            </div>

          </div>

        ) : (

          <div className="rounded-xl bg-secondary/30 py-12 px-4 text-center">

            <Wheat className="h-7 w-7 text-muted-foreground mx-auto mb-3" />


            <p className="font-semibold text-[hsl(148,38%,16%)]">
              No active stored-feed usage found
            </p>


            <p className="text-sm text-muted-foreground mt-1">
              Assign an active ration to an animal group to calculate feed usage.
            </p>

          </div>

        )}

      </Card>


      {/* ================================================= */}
      {/* GROUP BREAKDOWN                                  */}
      {/* ================================================= */}

      <div className="flex items-center gap-2 mb-3">

        <Boxes className="h-5 w-5 text-primary" />


        <div>

          <h2 className="font-display text-lg font-bold text-[hsl(148,38%,16%)]">
            Feed use by group
          </h2>


          <p className="text-xs text-muted-foreground">
            Expand a feed to see which animal groups are using it.
          </p>

        </div>

      </div>


      <Card className="overflow-x-auto mb-6">

        <Table>

          <TableHeader>

            <TableRow>

              <TableHead className="w-8" />

              <TableHead>
                Feed
              </TableHead>

              <TableHead className="text-right">
                Current stock
              </TableHead>

              <TableHead className="text-right">
                Current use / day
              </TableHead>

              <TableHead className="text-right">
                Days left
              </TableHead>

            </TableRow>

          </TableHeader>


          <TableBody>

            {storedRows.map(
              (
                row
              ) => {
                const groups =
                  Array.isArray(
                    row.groups
                  )
                    ? row.groups
                    : [];


                const dailyFresh =
                  getDailyFreshTonnes(
                    row
                  );


                const stock =
                  getCurrentStockTonnes(
                    row
                  );


                const status =
                  STATUS[
                    row.status
                  ] ||
                  STATUS.unknown;


                const isOpen =
                  Boolean(
                    expanded[
                      row.id
                    ]
                  );


                return (
                  <Fragment
                    key={
                      row.id
                    }
                  >

                    <TableRow>

                      <TableCell>

                        {groups.length >
                          0 && (

                          <button
                            type="button"
                            onClick={() =>
                              toggle(
                                row.id
                              )
                            }
                            className="text-muted-foreground hover:text-primary"
                          >

                            {isOpen ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}

                          </button>

                        )}

                      </TableCell>


                      <TableCell>

                        <div className="flex flex-wrap items-center gap-2">

                          <span className="font-medium">
                            {row.name}
                          </span>


                          <span className={`rounded-full px-2 py-0.5 text-[9px] uppercase font-semibold ${status.cls}`}>
                            {status.label}
                          </span>

                        </div>

                      </TableCell>


                      <TableCell className="text-right tabular-nums">

                        {stock !=
                        null
                          ? `${formatNumber(
                              stock,
                              1
                            )} t`
                          : "—"}

                      </TableCell>


                      <TableCell className="text-right tabular-nums">

                        {dailyFresh >
                        0
                          ? `${formatNumber(
                              dailyFresh,
                              2
                            )} t`
                          : "—"}

                      </TableCell>


                      <TableCell className="text-right tabular-nums">

                        {row.days_remaining !=
                        null
                          ? `${formatNumber(
                              row.days_remaining,
                              1
                            )} d`
                          : "—"}

                      </TableCell>

                    </TableRow>


                    {isOpen &&
                      groups.length >
                        0 && (

                      <TableRow>

                        <TableCell />


                        <TableCell
                          colSpan={4}
                          className="bg-secondary/30"
                        >

                          <div className="py-2 space-y-2">

                            {groups.map(
                              (
                                group
                              ) => (

                                <div
                                  key={
                                    group.group_id
                                  }
                                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 text-sm"
                                >

                                  <span className="font-medium">
                                    {group.group_name}
                                  </span>


                                  <span className="text-muted-foreground tabular-nums">

                                    {formatNumber(
                                      group.daily_dm,
                                      0
                                    )}{" "}

                                    kg DM/day

                                    {" · "}

                                    {formatNumber(
                                      group.tonnes_per_day,
                                      2
                                    )}{" "}

                                    t fresh/day

                                  </span>

                                </div>

                              )
                            )}

                          </div>

                        </TableCell>

                      </TableRow>

                    )}

                  </Fragment>
                );
              }
            )}

          </TableBody>

        </Table>

      </Card>


      {/* ================================================= */}
      {/* FEED USAGE HISTORY                                */}
      {/* ================================================= */}

      <Card className="p-5 sm:p-6">

        <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-5">

          <div className="flex items-start gap-3">

            <div className="h-10 w-10 rounded-full bg-primary/10 grid place-items-center shrink-0">

              <CalendarDays className="h-5 w-5 text-primary" />

            </div>


            <div>

              <h2 className="font-display text-lg font-bold text-[hsl(148,38%,16%)]">
                Feed usage history
              </h2>


              <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                Track feed used and feed expenditure across the year.
              </p>

            </div>

          </div>


          <div className="flex flex-wrap gap-2">

            <select
              value={
                historyYear
              }
              onChange={(
                event
              ) =>
                setHistoryYear(
                  Number(
                    event.target
                      .value
                  )
                )
              }
              className="h-9 rounded-lg border bg-background px-3 text-sm"
            >

              {availableYears.map(
                (
                  year
                ) => (

                  <option
                    key={
                      year
                    }
                    value={
                      year
                    }
                  >
                    {year}
                  </option>

                )
              )}

            </select>


            <select
              value={
                historyFeed
              }
              onChange={(
                event
              ) =>
                setHistoryFeed(
                  event.target
                    .value
                )
              }
              className="h-9 rounded-lg border bg-background px-3 text-sm min-w-[160px]"
            >

              <option value="all">
                All feeds
              </option>


              {historyFeeds.map(
                (
                  feed
                ) => (

                  <option
                    key={
                      feed.feed_id
                    }
                    value={
                      feed.feed_id
                    }
                  >
                    {feed.feed_name}
                  </option>

                )
              )}

            </select>

          </div>

        </div>


        <div className="flex flex-wrap gap-2 mt-6">

          {[
            {
              key:
                "fresh",

              label:
                "Fresh tonnes",
            },

            {
              key:
                "dm",

              label:
                "DM tonnes",
            },

            {
              key:
                "cost",

              label:
                "Cost",
            },
          ].map(
            (
              metric
            ) => {
              const active =
                historyMetric ===
                metric.key;


              return (

                <button
                  key={
                    metric.key
                  }
                  type="button"
                  onClick={() =>
                    setHistoryMetric(
                      metric.key
                    )
                  }
                  className={
                    active
                      ? "rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground"
                      : "rounded-full bg-secondary px-4 py-2 text-xs font-semibold text-muted-foreground hover:text-foreground"
                  }
                >
                  {metric.label}
                </button>

              );
            }
          )}

        </div>


        {historyFetching && (

          <p className="text-xs text-muted-foreground mt-4">
            Updating feed history…
          </p>

        )}


        {historyError ? (

          <div className="rounded-xl bg-secondary/30 p-8 text-center mt-6">

            <p className="font-semibold">
              Feed history unavailable
            </p>

            <p className="text-sm text-muted-foreground mt-1">
              The feed ledger could not be loaded.
            </p>

          </div>

        ) : historyData?.records >
          0 ? (

          <>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">

              <div className="rounded-xl border bg-primary/[0.025] p-4">

                <p className="text-xs text-muted-foreground">
                  {historyYear} total
                </p>

                <p className="font-display text-2xl font-extrabold text-[hsl(148,38%,16%)] mt-1 tabular-nums">

                  {historyMetric ===
                  "cost"
                    ? `€${formatNumber(
                        historyTotal,
                        2
                      )}`
                    : `${formatNumber(
                        historyTotal,
                        2
                      )} t`}

                </p>

                <p className="text-xs text-muted-foreground mt-1">
                  {selectedHistoryFeed
                    ?.feed_name ||
                    "All feeds"}
                </p>

              </div>


              <div className="rounded-xl border p-4">

                <p className="text-xs text-muted-foreground">
                  Recording started
                </p>

                <p className="font-display text-xl font-bold text-[hsl(148,38%,16%)] mt-1">
                  {formatDate(
                    historyData
                      ?.recording_started
                  )}
                </p>

                <p className="text-xs text-muted-foreground mt-1">
                  Earliest ledger record
                </p>

              </div>


              <div className="rounded-xl border p-4">

                <p className="text-xs text-muted-foreground">
                  Latest record
                </p>

                <p className="font-display text-xl font-bold text-[hsl(148,38%,16%)] mt-1">
                  {formatDate(
                    historyData
                      ?.recording_latest
                  )}
                </p>

                <p className="text-xs text-muted-foreground mt-1">
                  {
                    historyData
                      ?.records
                  }{" "}
                  ledger records
                </p>

              </div>

            </div>


            <div className="mt-7">

              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mb-4">

                <div>

                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                    Monthly usage
                  </p>

                  <p className="font-semibold text-[hsl(148,38%,16%)] mt-1">
                    {selectedHistoryFeed
                      ?.feed_name ||
                      "All feeds"}
                  </p>

                </div>


                <p className="text-xs text-muted-foreground">
                  {historyMetric ===
                  "cost"
                    ? "Historical feed cost"
                    : historyMetric ===
                      "dm"
                    ? "Tonnes dry matter"
                    : "Tonnes fresh weight"}
                </p>

              </div>


              <div className="w-full h-[300px] min-w-0">

                <ResponsiveContainer
                  width="100%"
                  height="100%"
                >

                  <LineChart
                    data={
                      historyChartData
                    }
                    margin={{
                      top:
                        10,

                      right:
                        15,

                      bottom:
                        5,

                      left:
                        5,
                    }}
                  >

                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={
                        false
                      }
                      opacity={
                        0.35
                      }
                    />


                    <XAxis
                      dataKey="month"
                      axisLine={
                        false
                      }
                      tickLine={
                        false
                      }
                      tick={{
                        fontSize:
                          11,
                      }}
                    />


                    <YAxis
                      axisLine={
                        false
                      }
                      tickLine={
                        false
                      }
                      width={
                        58
                      }
                      tick={{
                        fontSize:
                          10,
                      }}
                      tickFormatter={(
                        value
                      ) =>
                        historyMetric ===
                        "cost"
                          ? `€${formatNumber(
                              value,
                              0
                            )}`
                          : formatNumber(
                              value,
                              1
                            )
                      }
                    />


                    <Tooltip
                      formatter={(
                        value
                      ) => [
                        historyMetric ===
                        "cost"
                          ? `€${formatNumber(
                              value,
                              2
                            )}`
                          : `${formatNumber(
                              value,
                              2
                            )} t`,

                        historyMetric ===
                        "cost"
                          ? "Cost"
                          : historyMetric ===
                            "dm"
                          ? "DM used"
                          : "Fresh used",
                      ]}
                    />


                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="hsl(148, 42%, 36%)"
                      strokeWidth={
                        3
                      }
                      dot={{
                        r:
                          4,
                      }}
                      activeDot={{
                        r:
                          6,
                      }}
                      connectNulls
                    />

                  </LineChart>

                </ResponsiveContainer>

              </div>

            </div>


            <div className="mt-7">

              <div className="mb-3">

                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  Annual feed use
                </p>

                <p className="text-xs text-muted-foreground mt-1">
                  Usage and historical expenditure recorded during {historyYear}.
                </p>

              </div>


              <div className="rounded-xl border overflow-x-auto">

                <Table>

                  <TableHeader>

                    <TableRow>

                      <TableHead>
                        Feed
                      </TableHead>

                      <TableHead className="text-right">
                        Fresh used
                      </TableHead>

                      <TableHead className="text-right">
                        DM used
                      </TableHead>

                      <TableHead className="text-right">
                        Cost
                      </TableHead>

                    </TableRow>

                  </TableHeader>


                  <TableBody>

                    {historyFeeds.map(
                      (
                        feed
                      ) => (

                        <TableRow
                          key={
                            feed.feed_id
                          }
                          className="cursor-pointer hover:bg-secondary/30"
                          onClick={() =>
                            setHistoryFeed(
                              feed.feed_id
                            )
                          }
                        >

                          <TableCell className="font-semibold text-[hsl(148,38%,16%)]">
                            {feed.feed_name}
                          </TableCell>


                          <TableCell className="text-right tabular-nums">
                            {formatNumber(
                              feed.fresh_tonnes,
                              2
                            )}{" "}
                            t
                          </TableCell>


                          <TableCell className="text-right tabular-nums">
                            {formatNumber(
                              feed.dm_tonnes,
                              2
                            )}{" "}
                            t
                          </TableCell>


                          <TableCell className="text-right tabular-nums font-semibold">
                            €{formatNumber(
                              feed.cost,
                              2
                            )}
                          </TableCell>

                        </TableRow>

                      )
                    )}

                  </TableBody>

                </Table>

              </div>

            </div>


            <div className="rounded-xl bg-secondary/40 p-4 mt-5">

              <div className="flex gap-3 items-start">

                <Info className="h-4 w-4 text-primary shrink-0 mt-0.5" />


                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">

                  Feed use is recorded from the Farm Hand feed ledger.
                  Farmer-confirmed usage can replace estimated ration usage
                  for the same feed and day. Feed expenditure uses the
                  historical feed price that applied on the date of feeding,
                  rather than the feed's current price.

                </p>

              </div>

            </div>

          </>

        ) : (

          <div className="rounded-xl bg-secondary/30 py-12 px-4 text-center mt-6">

            <CalendarDays className="h-7 w-7 text-muted-foreground mx-auto mb-3" />

            <p className="font-semibold text-[hsl(148,38%,16%)]">
              No feed history for {historyYear}
            </p>

            <p className="text-sm text-muted-foreground mt-1">
              Feed usage will appear here as Farm Hand records the daily feed ledger.
            </p>

          </div>

        )}

      </Card>

    </div>
  );
}